import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { TimestampRange } from '../../components/time-picker/types';
import { ApiService } from '../api/api.service';
import { TimeMode } from '../inputs.datastore/types';
import { KPIDetails } from './types';

@Injectable({
  providedIn: 'root'
})
export class KpiDetailsService {
  realtimemonitorcache: KPIDetails[] = [];
  cache = new BehaviorSubject<KPIDetails[]>([]);
  getKpiDetailsPath = '/anomaly/v3/kpidetails';
  kpiDetails: KPIDetails[] = [];

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(private readonly apiService: ApiService) {}

  getEntityName(): string {
    return 'kpidetails';
  }

  getKpiDetails$(): Observable<KPIDetails[]> {
    return this.apiService.get$(this.getKpiDetailsPath, {
      criteria: { must_not: [{ exact: { state: 'destroyed' } }] }
    });
  }

  getValues(): KPIDetails[] {
    return this.getKpiDetails();
  }

  getValuesAsync(): Observable<KPIDetails[]> {
    const kpiDetails = this.getValues();
    if (!kpiDetails || kpiDetails.length === 0) {
      return this.updateKpiDetails(this.realtimemonitorcache).pipe(first());
    }
    return of(kpiDetails).pipe(first());
  }

  getKpiDetails(): KPIDetails[] {
    return this.chooseCacheModel();
  }

  chooseCacheModel(): KPIDetails[] {
    // simplified implementation/no support for history mode
    return this.realtimemonitorcache;
  }

  updateKpiDetails(
    cache: KPIDetails[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params?: TimestampRange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    firstRequest?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mode?: TimeMode
  ): Subject<KPIDetails[]> {
    const updated = new Subject<KPIDetails[]>();
    this.getKpiDetails$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response && response.length) {
            this.kpiDetails = [];
            response.forEach(data => {
              this.kpiDetails.push({
                kpi: data.kpi,
                unit: data.unit
              });
            });

            this.assignKpiDetails(cache, this.kpiDetails);
            updated.next(this.kpiDetails);
            return this.chooseCacheModel();
          }
        },
        () => {},
        () => {
          updated.complete();
        }
      );
    return updated;
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  assignKpiDetails(cache: KPIDetails[], data: KPIDetails[]): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of data) {
      cache.push(entry);
    }

    this.cache.next(this.chooseCacheModel());
  }
}
